import React, { useMemo, useState, useEffect } from "react";
import dayjs from "dayjs";
import { Pencil, Trash2, X, Check } from "lucide-react";

const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";

const STATUS_STYLE = {
  Approved:  "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  Pending:   "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  Rejected:  "text-red-400 bg-red-500/10 border-red-500/20",
  Cancelled: "text-white/30 bg-white/5 border-white/10",
};

const normalize = r => ({
  id:         r?.id ?? r?._id ?? Math.random(),
  leave_type: r?.leave_type ?? r?.type ?? "—",
  start_date: r?.start_date ?? r?.from ?? "",
  end_date:   r?.end_date   ?? r?.to   ?? "",
  reason:     r?.reason ?? "",
  status:     r?.status ?? "Pending",
});

const fmt  = d => d ? dayjs(d).format("DD MMM YYYY") : "—";
const dur  = (s, e) => { if (!s || !e) return "—"; return Math.max(1, dayjs(e).diff(dayjs(s), "day") + 1) + "d"; };
const canAct = (l) => l.status === "Pending" || (l.status === "Approved" && l.start_date && dayjs().isBefore(dayjs(l.start_date), "day"));

export default function LeaveHistoryTable({ leaveRecords, loading, onModifyConfirmed, onCancelConfirmed }) {
  const [localData, setLocalData] = useState(() => (Array.isArray(leaveRecords) ? leaveRecords : []).map(normalize));
  const records = useMemo(() => localData.map(normalize), [localData]);

  // Sync prop → state when the parent's async fetch resolves.  Without
  // this, the useState initialiser only ran once at mount (with leaveRecords
  // === []) and the table stayed empty even after the API returned data.
  useEffect(() => {
    setLocalData(
      Array.isArray(leaveRecords) ? leaveRecords.map(normalize) : []
    );
  }, [leaveRecords]);

  const [editTarget, setEditTarget]   = useState(null);
  const [editDraft,  setEditDraft]    = useState({});
  const [editError,  setEditError]    = useState("");
  const [cancelTarget, setCancelTarget] = useState(null);

  const openEdit = (leave) => { setEditTarget(leave); setEditDraft({ start_date: leave.start_date, end_date: leave.end_date, reason: leave.reason }); setEditError(""); };
  const closeEdit = () => { setEditTarget(null); setEditError(""); };

  const submitEdit = async () => {
    if (!editDraft.start_date || !editDraft.end_date) { setEditError("Start and end dates are required."); return; }
    if (dayjs(editDraft.end_date).isBefore(dayjs(editDraft.start_date), "day")) { setEditError("End date must be after start date."); return; }
    try {
      await Promise.resolve(onModifyConfirmed?.(editTarget, editDraft));
      setLocalData(prev => prev.map(r => r.id === editTarget.id ? { ...r, ...editDraft } : r));
      closeEdit();
    } catch (e) { setEditError(e?.message || "Save failed."); }
  };

  const confirmCancel = async () => {
    try {
      await Promise.resolve(onCancelConfirmed?.(cancelTarget));
      setLocalData(prev => prev.map(r => r.id === cancelTarget.id ? { ...r, status: "Cancelled" } : r));
    } finally { setCancelTarget(null); }
  };

  return (
    <>
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Leave History</span>
          <span className="text-xs text-white/30">{records.length} records</span>
        </div>

        <div className="w-full">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5">
                {["Type","From","To","Duration","Status","Reason",""].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs text-[#00ff90]/40 uppercase tracking-wider font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [1, 2, 3].map(i => (
                  <tr key={`sk-${i}`} className="border-b border-white/5">
                    {[80, 60, 60, 40, 70, 100, 30].map((w, j) => (
                      <td key={j} className="px-4 py-3">
                        <div style={{
                          width: w, height: 10, borderRadius: 4,
                          background: "linear-gradient(90deg, #111 25%, #1a1a1a 50%, #111 75%)",
                          backgroundSize: "200% 100%",
                          animation: "shimmer 1.5s linear infinite",
                        }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : records.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-white/20 text-sm italic">No leave applications yet.</td></tr>
              ) : records.map(leave => (
                <tr key={leave.id} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                  <td className="px-4 py-3 text-white/70 capitalize whitespace-nowrap">{leave.leave_type}</td>
                  <td className="px-4 py-3 text-white/60 text-xs whitespace-nowrap">{fmt(leave.start_date)}</td>
                  <td className="px-4 py-3 text-white/60 text-xs whitespace-nowrap">{fmt(leave.end_date)}</td>
                  <td className="px-4 py-3 text-white/40 text-xs whitespace-nowrap">{dur(leave.start_date, leave.end_date)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-block whitespace-nowrap text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[leave.status] || "text-white/30 bg-white/5 border-white/10"}`}>
                      {leave.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-white/40 text-xs truncate max-w-[120px]">{leave.reason || "—"}</td>
                  <td className="px-4 py-3">
                    {canAct(leave) ? (
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(leave)} className="text-blue-400/60 hover:text-blue-400 transition-colors"><Pencil size={13} /></button>
                        <button onClick={() => setCancelTarget(leave)} className="text-red-400/60 hover:text-red-400 transition-colors"><Trash2 size={13} /></button>
                      </div>
                    ) : <span className="text-white/15 text-xs">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit modal */}
      {editTarget && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4">
          <div className="w-full max-w-md rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_40px_#00ff9022]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
              <h3 className="text-sm font-semibold text-white">Modify Leave</h3>
              <button onClick={closeEdit} className="w-8 h-8 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/40 hover:text-white transition-colors"><X size={14} /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5">Leave Type</label>
                <input className={`${inputCls} opacity-50 cursor-not-allowed`} value={editTarget.leave_type} disabled />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5">Start Date</label>
                  <input type="date" className={inputCls} value={editDraft.start_date}
                    onChange={e => setEditDraft(d => ({ ...d, start_date: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5">End Date</label>
                  <input type="date" className={inputCls} value={editDraft.end_date}
                    onChange={e => setEditDraft(d => ({ ...d, end_date: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className="text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5">Reason</label>
                <textarea className={`${inputCls} resize-none`} rows={2} placeholder="Optional…" value={editDraft.reason}
                  onChange={e => setEditDraft(d => ({ ...d, reason: e.target.value }))} />
              </div>
              {editError && <p className="text-red-400 text-xs">⚠ {editError}</p>}
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={closeEdit} className="text-xs px-4 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
                <button onClick={submitEdit} className="text-xs px-4 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 transition-colors">Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel confirm */}
      {cancelTarget && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4">
          <div className="w-full max-w-sm rounded-xl border border-red-500/30 bg-[#0d0d0d] p-6">
            <h3 className="text-sm font-semibold text-white mb-2">Cancel Leave</h3>
            <p className="text-white/50 text-sm">Cancel <span className="text-white">{cancelTarget.leave_type}</span> leave starting <span className="text-white">{fmt(cancelTarget.start_date)}</span>?</p>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setCancelTarget(null)} className="text-xs px-4 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Dismiss</button>
              <button onClick={confirmCancel} className="text-xs px-4 py-2 rounded-lg bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25 transition-colors">Yes, Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}